import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    colors, fontSizes, fontWeights, space, screenReaderOnlyStyle
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import { getImageAltText } from 'utils/Accessibility';
import {
    Grid, Box, Text, Button, Flex, Link
} from 'components/ui';
import Select from 'components/Inputs/Select/Select';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import { getArQuantityRange } from 'utils/arQuantity';

const LEFT_COLUMN_WIDTH = 108;

/**
 * Displays a single subscription product with its image, brand, name, variation,
 * pricing, and an optional SKU swap trigger.
 */
function SubscriptionProduct({
    product, shouldShowItemVariation, localization, onSkuSwapClick, isSkuSwapEnabled, isQtyPickerEnabled, onQtyChange
}) {
    const handleSkuSwapClick = useCallback(() => {
        onSkuSwapClick?.({ swapOutProduct: product });
    }, [onSkuSwapClick, product]);

    const qtyId = `ms_qty_${product.skuId}`;
    const qtyRange = getArQuantityRange(product);
    const showQtyPicker = isQtyPickerEnabled && !product.acceleratedPromotion;
    const handleQtyChange = useCallback(
        event => onQtyChange?.(Number(event.target.value)),
        [onQtyChange]
    );

    return (
        <Grid css={styles.productContainer}>
            <Box>
                <ProductImage
                    id={product.skuId}
                    size={LEFT_COLUMN_WIDTH}
                    skuImages={product.skuImages}
                    disableLazyLoad={true}
                    altText={getImageAltText(product)}
                />
            </Box>
            <Box>
                <Grid css={[styles.productDetails, styles.baseFont]}>
                    <Box>
                        <Text
                            is='h4'
                            css={styles.title}
                            children={product.brandName}
                            data-at={Sephora?.debug?.dataAt('sku_brand')}
                        />
                        <Text
                            is='p'
                            css={styles.subtitle}
                            children={product.productName}
                            data-at={Sephora?.debug?.dataAt('sku_name')}
                        />
                        {shouldShowItemVariation && (
                            <Text
                                is='p'
                                css={[styles.sku, styles.secondaryColor]}
                                data-at={Sephora?.debug?.dataAt('sku_size')}
                            >
                                {product.variationType}: {product.variationValue}
                            </Text>
                        )}
                        <Box>
                            <Text
                                css={styles.priceSaving}
                                data-at={Sephora?.debug?.dataAt('sku_discounted_price')}
                            >
                                {product.savingAmount}
                            </Text>{' '}
                            <Text
                                is='del'
                                data-at={Sephora?.debug?.dataAt('sku_price')}
                            >
                                {product.productPrice}
                            </Text>
                        </Box>
                        {showQtyPicker ? (
                            <Flex css={styles.qtySwapRow}>
                                <Box>
                                    <label
                                        htmlFor={qtyId}
                                        children={localization.qty}
                                        css={screenReaderOnlyStyle}
                                    />
                                    <Select
                                        id={qtyId}
                                        size='xs'
                                        value={product.qty}
                                        onChange={handleQtyChange}
                                        marginBottom={null}
                                        customStyle={{ input: styles.qtySelect }}
                                        data-at={Sephora?.debug?.dataAt('ms_sku_qty')}
                                    >
                                        {qtyRange.map(option => (
                                            <option
                                                key={option.toString()}
                                                value={option}
                                                children={option}
                                            />
                                        ))}
                                    </Select>
                                </Box>
                                {!!(isSkuSwapEnabled && onSkuSwapClick) && (
                                    product.productHasSeveralSkus ? (
                                        <Button
                                            variant='secondary'
                                            size='sm'
                                            onClick={handleSkuSwapClick}
                                            data-testid='sku-swap-trigger'
                                            css={styles.swapButton}
                                            children={localization.skuSwapButton}
                                        />
                                    ) : (
                                        <Text
                                            is='p'
                                            css={[styles.skuSwap, styles.secondaryColor]}
                                            children={localization.skuSwapUnavailable}
                                        />
                                    )
                                )}
                            </Flex>
                        ) : (
                            !!(isSkuSwapEnabled && onSkuSwapClick) && (
                                <Text
                                    is='p'
                                    css={[styles.legacySkuSwap, styles.secondaryColor]}
                                >
                                    {product.productHasSeveralSkus ? (
                                        <Link
                                            css={styles.linkColor}
                                            children={localization.skuSwapButton}
                                            onClick={handleSkuSwapClick}
                                            data-testid='sku-swap-trigger'
                                        />
                                    ) : (
                                        localization.skuSwapUnavailable
                                    )}
                                </Text>
                            )
                        )}
                    </Box>
                </Grid>
            </Box>
        </Grid>
    );
}

const styles = {
    productContainer: {
        alignItems: 'start',
        gap: space[3],
        gridTemplateColumns: 'auto 1fr',
        marginBottom: space[4],
        marginTop: space[3]
    },
    productDetails: {
        gridTemplateColumns: 'auto 1fr'
    },
    title: {
        fontWeight: fontWeights.bold,
        marginBottom: space[1] / 2
    },
    subtitle: {
        marginBottom: space[1] / 2
    },
    sku: {
        marginBottom: space[2]
    },
    priceSaving: {
        fontWeight: fontWeights.bold,
        color: colors.red
    },
    skuSwap: {
        alignSelf: 'center'
    },
    legacySkuSwap: {
        marginTop: space[2]
    },
    linkColor: {
        color: colors.blue
    },
    qtySwapRow: {
        alignItems: 'center',
        gap: space[2],
        marginTop: space[2]
    },
    qtySelect: {
        borderColor: colors.midGray
    },
    swapButton: {
        height: 24,
        minHeight: 24,
        fontSize: fontSizes.sm,
        fontWeight: fontWeights.normal,
        border: `1px solid ${colors.midGray}`
    },

    // Shared/Utility
    baseFont: {
        fontSize: fontSizes.base
    },
    secondaryColor: {
        color: colors.gray
    }
};

SubscriptionProduct.propTypes = {
    product: PropTypes.shape({
        skuId: PropTypes.string,
        brandName: PropTypes.string,
        productName: PropTypes.string,
        variationType: PropTypes.string,
        variationValue: PropTypes.string,
        savingAmount: PropTypes.string,
        productPrice: PropTypes.string,
        skuImages: PropTypes.object,
        productHasSeveralSkus: PropTypes.bool
    }).isRequired,
    shouldShowItemVariation: PropTypes.bool.isRequired,
    localization: PropTypes.shape({
        skuSwapButton: PropTypes.string,
        skuSwapUnavailable: PropTypes.string
    }).isRequired,
    onSkuSwapClick: PropTypes.func,
    isSkuSwapEnabled: PropTypes.bool,
    isQtyPickerEnabled: PropTypes.bool,
    onQtyChange: PropTypes.func
};

export default wrapFunctionalComponent(SubscriptionProduct, 'SubscriptionProduct');
